import React, { useCallback, useState,useMemo} from 'react';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import ChassisMultiSelect from '../../../../components/common/ChassisDropdownWithMultiSelect';
import DealerDropdown from '../../../../components/common/DealerDropdownWithMulti';
import CountryDropdownWithMultiSelect from '../../../../components/common/CountryDropdownWithMultiSelect';
import { useLanguage } from '../../../../contexts/LanguageContext';
import ModelDropdown from '../../../../components/common/ModelDropdown';
import WarrantyTypeDropdownWithMulti from '../../../../components/common/WarrantyTypeDropdownWithMulti';
import { Chips } from 'primereact/chips';
import { Roles } from '../../../../utils/roleEnums';
import { usePermissions } from '../../../../hooks/usePermissions';
import { Dropdown } from 'primereact/dropdown';



export const CLAIM_STATUS = {
    ACCEPTED: 66,
    REJECTED: 67,
};
const initialFilters = {
    dealer: { isAllDealers: false, dealerIds: [] },
    dealerCode: [],
    sasiNo: [],
    country: [],
    warrClaimNo: null,
    workOrderNo: null,
    modelName: null,
    warrTypeList: [],
    workOrderNoStart: null,
    workOrderNoEnd: null,
    sentToApprovalStartDate: null, 
    sentToApprovalEndDate: null, 
    claimStatus: null,
    code: null,
    licensePlate: null,
};

export default function VehicleWarrantyClaimsFilter({ onApplyFilters, onResetFilters }) {
  const { th } = useLanguage();
  const { hasAnyRole } = usePermissions();
  const isDealer = hasAnyRole([Roles.DEALER, Roles.SERVICE_DEALER, Roles.SALES_DEALER]);


  const [filterOpen, setFilterOpen] = useState(true);
  const [filters, setFilters] = useState(initialFilters);

  const toggleOpen = useCallback(() => setFilterOpen((p) => !p), []);

    const setField = useCallback((field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    }, []);

  const statusOptions = useMemo(() => [
    { label: th('vehicleWarrantyClaims.filter.statusAccepted'), value: CLAIM_STATUS.ACCEPTED },
    { label: th('vehicleWarrantyClaims.filter.statusRejected'), value: CLAIM_STATUS.REJECTED },
], [th]);

   const formatDateToYYYYMMDD = (date) => {
    if (!date) return undefined;
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}${month}${day}`; 
};

    const formattedFilters = (filters) => {
        const { dealer, dealerCode, sasiNo, country, warrClaimNo, workOrderNo, modelName, warrTypeList, workOrderNoStart, workOrderNoEnd, sentToApprovalStartDate, sentToApprovalEndDate, claimStatus, code, licensePlate } = filters;

        return {
            dealer: !dealer?.isAllDealers && dealer?.dealerIds?.length > 0 ? dealer.dealerIds.map(String) : undefined,
            dealerCode: dealerCode?.length > 0 ? dealerCode : undefined, 
            sasiNo: sasiNo?.length > 0 ? sasiNo : undefined,
            country: country?.length > 0 ? country : undefined,
            warrClaimNo: warrClaimNo?.trim() || undefined,
            workOrderNo: workOrderNo ?? undefined,
            modelName: modelName?.name || undefined,
            warrTypeList: warrTypeList?.length > 0 ? warrTypeList : undefined,
            workOrderNoStart: workOrderNoStart ?? undefined,
            workOrderNoEnd: workOrderNoEnd ?? undefined,
            sentToApprovalStartDate: sentToApprovalStartDate || undefined,
            sentToApprovalEndDate: sentToApprovalEndDate || undefined,
           claimStatus: claimStatus ?? undefined,
            code: code?.trim() || undefined,
            licensePlate: licensePlate?.trim() || undefined,
        };
    };

    const apply = useCallback(() => {
        onApplyFilters?.(formattedFilters(filters));
    }, [filters, onApplyFilters]);

  const reset = useCallback(() => {
    setFilters(initialFilters);
    onResetFilters?.();
  }, [onResetFilters]);

  return (
      <div className="generic-table-filter">
          <div className="grid">
              <div className="col-12 md:col-6 flex justify-content-start">
                  <Button icon="pi pi-filter" label={th('vehicleWarrantyClaims.filter.advancedFilter')} onClick={toggleOpen} outlined={!filterOpen} />
              </div>
          </div>

          {filterOpen && (
              <div className="mt-3">
                  <Divider />
                  <div className="text-xl font-medium mb-3">{th('vehicleWarrantyClaims.filter.advancedFilters')}</div>

                  <div className="grid">
                      {/* Dealer */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.columns.dealerName')}</label>
                              <DealerDropdown value={filters.dealer} onChange={(val) => setField('dealer', val)} className="w-full" multiple showSelectAll rowCount={50} />
                          </div>
                      </div>
                      {/* Dealer Code */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.columns.dealerCode')}</label>
                              <Chips
                                  value={filters.dealerCode}
                                  onChange={(e) => setField('dealerCode', e.value)}
                                  className="w-full"
                                  style={{ width: '100%', display: 'flex' }}
                                  pt={{
                                      container: { style: { width: '100%', display: 'flex', flexWrap: 'wrap' } },
                                      input: { style: { flex: 1, minWidth: 0 } }
                                  }}
                                  placeholder={th('vehicleWarrantyClaims.filter.dealerCodePlaceholder')}
                                  separator=","
                              />
                          </div>
                      </div>
                      {/* Chassis Number */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.columns.chassisNumber')}</label>
                              <ChassisMultiSelect value={filters.sasiNo} onChange={(val) => setField('sasiNo', val)} className="w-full" placeholder={th('vehicleWarrantyClaims.filter.chassisNumberPlaceholder')} showDescription />
                          </div>
                      </div>
                      {/* Country */}
                      {!isDealer && (
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.columns.country')}</label>
                              <CountryDropdownWithMultiSelect value={filters.country} onChange={(val) => setField('country', val)} className="w-full" placeholder={th('vehicleWarrantyClaims.filter.countryPlaceholder')} showSelectAll />
                          </div>
                      </div>
                        )}
                      {/* Warranty Claim Number */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.columns.claimNumber')}</label>
                              <InputText value={filters.warrClaimNo ?? ''} onChange={(e) => setField('warrClaimNo', e.target.value)} className="w-full" placeholder={th('vehicleWarrantyClaims.filter.claimNumberPlaceholder')} />
                          </div>
                      </div>

                      {/* Work Order */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.columns.workOrder')}</label>
                              <input
                                  type="number"
                                  className="p-inputtext p-component w-full"
                                  value={filters.workOrderNo ?? ''}
                                  onChange={(e) => setField('workOrderNo', e.target.value === '' ? null : Number(e.target.value))}
                                  placeholder={th('vehicleWarrantyClaims.filter.workOrderPlaceholder')}
                                  min={0}
                              />
                          </div>
                      </div>
                      {/* Vehicle Model */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.columns.model')}</label>
                              <ModelDropdown value={filters.modelName} onChange={(val) => setField('modelName', val)} className="w-full" placeholder={th('customerAddressBrand.placeholder.model')}/>
                          </div>
                      </div>
                      {/* Warranty Type */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.columns.claimWarrantyType')}</label>
                              <WarrantyTypeDropdownWithMulti value={filters.warrTypeList} onChange={(val) => setField('warrTypeList', val)} className="w-full" multiple={true} showSelectAll={true} />
                          </div>
                      </div>
                       {/* Sent to Approval Start Date */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.filter.sentToApprovalStartDate')}</label>
                              <Calendar
                                  value={filters.sentToApprovalStartDate}
                                  onChange={(e) => setField('sentToApprovalStartDate', e.value)}
                                  dateFormat="dd.mm.yy"
                                  showIcon
                                  className="w-full"
                                  placeholder={th('vehicleWarrantyClaims.filter.selectDate')}
                              />
                          </div>
                      </div>

                      {/* Sent to Approval End Date */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.filter.sentToApprovalEndDate')}</label>
                              <Calendar
                                  value={filters.sentToApprovalEndDate}
                                  onChange={(e) => setField('sentToApprovalEndDate', e.value)}
                                  dateFormat="dd.mm.yy"
                                  showIcon
                                  className="w-full"
                                  placeholder={th('vehicleWarrantyClaims.filter.selectDate')}
                              />
                          </div>
                      </div>

                      {/* İş Emri Aralığı - Başlangıç */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.filter.workOrderRangeStart')}</label>
                              <input
                                  type="number"
                                  className="p-inputtext p-component w-full"
                                  value={filters.workOrderNoStart ?? ''}
                                  onChange={(e) => setField('workOrderNoStart', e.target.value === '' ? null : Number(e.target.value))}
                                  placeholder={th('vehicleWarrantyClaims.filter.workOrderPlaceholder')}
                                  min={0}
                              />
                          </div>
                      </div>

                      {/* İş Emri Aralığı - Bitiş */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.filter.workOrderRangeEnd')}</label>
                              <input
                                  type="number"
                                  className="p-inputtext p-component w-full"
                                  value={filters.workOrderNoEnd ?? ''}
                                  onChange={(e) => setField('workOrderNoEnd', e.target.value === '' ? null : Number(e.target.value))}
                                  placeholder={th('vehicleWarrantyClaims.filter.workOrderPlaceholder')}
                                  min={0}
                              />
                          </div>
                      </div>
                      {/* Claim Status */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.columns.claimStatus')}</label>
                              <Dropdown value={filters.claimStatus} options={statusOptions} onChange={(e) => setField('claimStatus', e.value)} className="w-full" placeholder={th('vehicleWarrantyClaims.filter.claimStatusPlaceholder')} showClear />
                          </div>
                      </div>
                      {/* Part/Labor Code */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.filter.code')}</label>
                              <InputText
                                  value={filters.code ?? ''}
                                  onChange={(e) => setField('code', e.target.value)}
                                  className="w-full"
                                  placeholder={th('vehicleWarrantyClaims.filter.codePlaceholder')}
                              />
                          </div>
                      </div>
                      {/* License Plate */}
                      <div className="col-12 md:col-6 lg:col-3">
                          <div className="field">
                              <label className="block mb-2 text-sm">{th('vehicleWarrantyClaims.filter.licensePlate')}</label>
                              <InputText
                                  value={filters.licensePlate ?? ''}
                                  onChange={(e) => setField('licensePlate', e.target.value)}
                                  className="w-full"
                                  placeholder={th('vehicleWarrantyClaims.filter.licensePlatePlaceholder')}
                              />
                          </div>
                      </div>
                  </div>

                  <div className="flex justify-content-start mt-3">
                      <Button label={th('vehicleWarrantyClaims.filter.reset')} icon="pi pi-refresh" className="p-button-outlined mr-2" onClick={reset} />
                      <Button label={th('vehicleWarrantyClaims.filter.apply')} icon="pi pi-check" onClick={apply} />
                  </div>
              </div>
          )}
      </div>
  );
}
